/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import { FieldData, SeedRecommendationOptimizationType, Recommendation, FormData } from '../../../types';
import { TargetYieldInputCell, SeedCostInputCell } from '../../CustomCells';
import { useFormContext, useForm, useWatch } from 'react-hook-form';
import { getCurrency } from 'user/userSelectors';
import { getBagSizeForCrop, getProductValidationValues } from 'user/selectors';
import GrainPriceInput from '../../GrainPriceInput';
import { RevertButton } from './RevertButton';
import Chart from './Chart';
import DetailsTable from './DetailsTable';
import { ChartDataSets, xDataKey } from './types';
import { LineChartProvider, InputChart, ResetButton, SliderChart } from '@tcc/fds-charts';
import { useSeedDensityRecommendation } from '../../../hooks/useSeedDensityRecommendation';
import { addErrorToast } from 'messages';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { convertForLocale } from 'util/unitConversion';
import { Spinner, ComponentSize } from '@tcc/fds';
import { useParseRecommendationRows } from '../useParseRecommendationRows';
import { useDetailsTable } from './DetailsTable/useDetailsTable';
import { RECOMMENDATION_GENERATION_PLANTING_RATE } from 'create/bulk/common/constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { getDraftSchema } from 'create/bulk/common/schemas/getSharedSchema';
import { debounce } from 'util/debounce';
import { getActiveCropNominalWeight } from 'current/currentSelectors';
import { getAvgFixedPopulationRateMin } from 'create/bulk/common/utils';
import { convertTargetYieldToMetric } from '../../utils';

interface RecommendationInspectorProps {
  field: FieldData;
  onClose: () => void;
  fieldIndex: number;
}

type LocalFormData = Pick<FieldData, 'seedCostPerBag' | 'targetYield' | 'avgFixedPopulation'>;

export const RecommendationInspector = ({ onClose, field, fieldIndex }: RecommendationInspectorProps) => {
  const cropNominalWeight = useAppSelector(getActiveCropNominalWeight);
  const bagSize = useAppSelector((state) => getBagSizeForCrop(state, 'corn'));
  const productValidationValues = useAppSelector(getProductValidationValues);
  const { RATE } = productValidationValues?.PLANTING?.CORN || {};
  const currency = useAppSelector(getCurrency);
  const firstRender = useRef(true);
  const dispatch = useAppDispatch();
  
  const { getValues: parentGetValues, setValue: parentSetValue } = useFormContext<FormData>();
  const [draftRecommendation, setDraftRecommendation] = useState<Recommendation>(field.recommendation);
  
  const { 
    getValues: localGetValues,
    setValue: localSetValue, 
    control, 
    formState: { errors, isDirty }
  } = useForm<LocalFormData>({
    mode: 'onChange',
    // @ts-expect-error
    resolver: yupResolver(getDraftSchema({ productValidationValues, currency })),
    defaultValues: {
      seedCostPerBag: field.seedCostPerBag,
      targetYield: field.targetYield,
      avgFixedPopulation: field.avgFixedPopulation,
    },
  });
  
  // subscribe to input changes in the local form
  const [watchedSeedCost, watchedPopulation, watchedTargetYield] = useWatch({
    control,
    name: ['seedCostPerBag', 'avgFixedPopulation', 'targetYield']
  });
  
  const { fvssRow, yourEntryRow } = useParseRecommendationRows({
    ...field,
    recommendation: draftRecommendation,
    seedCostPerBag: watchedSeedCost,
    avgFixedPopulation: watchedPopulation 
  });
  
  const table = useDetailsTable({ fvssRow, yourEntryRow });
    
  const datasets: ChartDataSets = useMemo(() => draftRecommendation.curve.plantingRate.map((rate, index) => ({
    plantingRate: convertForLocale({
      quantity: rate,
      unit: draftRecommendation.curve.plantingRateUnit,
      unitType: 'seed-density',
      cropNominalWeight,
    }).scalar,
    yield: convertForLocale({
      quantity: draftRecommendation.curve.yield[index],
      unit: draftRecommendation.curve.yieldUnit,
      unitType: 'area-density.yield',
      cropNominalWeight,
    }).scalar,
  })), [
    draftRecommendation.curve.plantingRate,
    draftRecommendation.curve.plantingRateUnit,
    draftRecommendation.curve.yield,
    draftRecommendation.curve.yieldUnit, 
    cropNominalWeight
  ]);
  
  
  // recommendation dot
  const fixedDots = useMemo(() => [
    {
      x: fvssRow.avgFixedPop,
      y: fvssRow.estimatedYield,
    },
  ], [fvssRow.avgFixedPop, fvssRow.estimatedYield]);
  
  // set the dynamic dot position based on the avg fixed population set by the user in the table
  const initialDynamicDots = useMemo(() => [{
    x: yourEntryRow.avgFixedPop,
    y: yourEntryRow.estimatedYield,
  }], [yourEntryRow.avgFixedPop, yourEntryRow.estimatedYield]);
  
  const { generateRecommendation, isLoading } = useSeedDensityRecommendation({
    draft: true,
    fieldId: field.id,
    onSuccess: (data) => {
      if (data) {
        setDraftRecommendation(data);
      }
    },
    onError: () => {
      dispatch(
        addErrorToast(
          getMessage('bulk-fvss-script-generation-recommendation-generation-error', { fieldName: field.name })
        )
      );
    }
  });
  
  
  // handle the save action to update the form with the new inputs introduced in the modal
  const handleSaveDraft = useCallback(() => {
    const localValues = localGetValues();
      
    parentSetValue(`fields.${fieldIndex}.targetYield`, localValues.targetYield);
    parentSetValue(`fields.${fieldIndex}.seedCostPerBag`, localValues.seedCostPerBag);
    parentSetValue(`fields.${fieldIndex}.avgFixedPopulation`, localValues.avgFixedPopulation);
    parentSetValue(`fields.${fieldIndex}.recommendation`, draftRecommendation);
      
    onClose();
  }, [localGetValues, parentSetValue, fieldIndex, draftRecommendation, onClose]);
  
  // cb that grabs the return of fds-chart and updates the form local state
  const handlePopulationChange = useCallback((newPopulation) => {
    localSetValue('avgFixedPopulation', newPopulation, { shouldDirty: true });
  }, [localSetValue]);
  
  // handle the revert button on each input to get back the value 
  // that was introduced in generation of the first recommendation
  const handleRevertInput = useCallback((fieldName: string) => {
    if (fieldName === 'targetYield') {
      localSetValue(`targetYield`, field.targetYield);
    } 
    if (fieldName === 'seedCostPerBag') {
      localSetValue(`seedCostPerBag`, field.seedCostPerBag);
    }
  }, [localSetValue, field.targetYield, field.seedCostPerBag]);
  
  // We request new recommendation on Target Yield change
  useEffect(() => {
    // Does not apply on first render
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
  
    // Does not apply if there is an error in the target yield input
    if(errors?.targetYield) return;
  
    // debounced to avoid multiple requests while user is changing the value
    const debouncedUpdate = debounce(() => {
      const metricTargetYield = convertTargetYieldToMetric(cropNominalWeight, Number(watchedTargetYield));

      generateRecommendation({
        recommendationTarget: {
          id: field.id,
          type: 'FIELD',
        },
        targetYield: {
          v: metricTargetYield.q,
          u: metricTargetYield.u,
        },
        seedProductId: parentGetValues(`fields.${fieldIndex}.hybrid`)?.id,
        optimizationType: SeedRecommendationOptimizationType.AGRONOMIC,
        plantingDayOfYear: new Date(parentGetValues('plannedPlantingDate')).toLocaleDateString('en-US', { 
          month: '2-digit', 
          day: '2-digit' 
        }).replace('/', '-') || '',
        plantingRateInput: {
          min: getAvgFixedPopulationRateMin(RATE),
          max: RECOMMENDATION_GENERATION_PLANTING_RATE.MAX,
          step: RECOMMENDATION_GENERATION_PLANTING_RATE.STEP,
        },
      },
      {
        seedsPerBag: bagSize,
        seedCostPerBag: Number(watchedSeedCost),
        cropNominalWeight,
        grainPrice: Number(parentGetValues('grainPrice')),
      });
    }, 1000);
  
    debouncedUpdate();
  
    return () => debouncedUpdate.cancel();
  }, [
    RATE,
    bagSize,
    cropNominalWeight,
    errors?.targetYield,
    field.id,
    fieldIndex,
    generateRecommendation,
    parentGetValues,
    watchedSeedCost,
    watchedTargetYield
  ]);
  
  return (
    <>
      <header>
        <div>
          <h2>{field.name}</h2>
          <span>{getMessage('text__field-level-seed-recs')}</span>
        </div>
        <Button onClick={onClose}>{getMessage('button-text__close')}</Button>
        <Button mode="primary" onClick={handleSaveDraft} disabled={isLoading || !isDirty}>
          {getMessage('button-text__save')}
        </Button>
      </header>
      {isLoading ? (
        <section className="loading-section">
          <Spinner size={ComponentSize.Lg} />
          <p>{getMessage('bulk-loading-subheader-text')}</p>
        </section>
      ) : (
        <LineChartProvider
          datasets={[datasets]}
          xDataKey={xDataKey.PLANTING_RATE}
          fixedDots={fixedDots}
          onUserChange={handlePopulationChange}
          initialDynamicDots={initialDynamicDots}
        >
          <section className="chart-section">
            <Chart />
          </section>
          <section className="details-section">
            <div className="left-column">
              <InputChart label={getMessage('rate')} showLabel> 
                <ResetButton className="revert-button">
                  {getMessage('revert-button')}
                </ResetButton>
              </InputChart>
              <TargetYieldInputCell
                control={control}
                name="targetYield"
                currency={currency}
                rowIndex={fieldIndex}
                fieldUuid={field.id}
                label={getMessage('target-dry-yield')}
              > 
                <RevertButton onClick={() => handleRevertInput('targetYield')} />
              </TargetYieldInputCell>
              <GrainPriceInput currency={currency} disabled />
              <SeedCostInputCell
                control={control}
                name="seedCostPerBag"
                currency={currency}
                bagSize={bagSize}
                rowIndex={fieldIndex}
                label={getMessage('price-product')}
              >
                <RevertButton onClick={() => handleRevertInput('seedCostPerBag')} />
              </SeedCostInputCell>
            </div>
            <div className="right-column">
              <SliderChart />
              <DetailsTable table={table} />
            </div>
          </section>
        </LineChartProvider>
      )}
    </>
  );
};
